/* eslint-disable no-restricted-syntax */
import { useCallback, useState } from "react";
import { useLocation } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { graphqlClient } from "@/graphql/client";
import { MessageSquarePlus, Send } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const SUBMIT_FEEDBACK_MUTATION = `
  mutation SubmitFeedback($input: FeedbackInput!) {
    submitFeedback(input: $input)
  }
`;

const sentiments = [
  { key: "love", emoji: "\u2764\uFE0F", label: "Love it" },
  { key: "good", emoji: "\uD83D\uDC4D", label: "Good" },
  { key: "meh", emoji: "\uD83E\uDD37", label: "Meh" },
  { key: "bad", emoji: "\uD83D\uDC4E", label: "Needs work" },
] as const;

type Sentiment = (typeof sentiments)[number]["key"];

export function FeedbackDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { pathname } = useLocation();
  const [sentiment, setSentiment] = useState<Sentiment | null>(null);
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const feedbackMutation = useMutation({
    mutationFn: (input: { sentiment: Sentiment; message: string; page: string }) =>
      graphqlClient.request(SUBMIT_FEEDBACK_MUTATION, { input }),
    onSuccess: () => {
      setSubmitted(true);
      setTimeout(() => {
        handleClose(false);
      }, 1400);
    },
  });

  const handleClose = useCallback(
    (next: boolean) => {
      onOpenChange(next);
      if (!next) {
        // Reset after close animation
        setTimeout(() => {
          setSentiment(null);
          setMessage("");
          setSubmitted(false);
        }, 200);
      }
    },
    [onOpenChange],
  );

  const handleSubmit = useCallback(() => {
    if (!sentiment) return;
    feedbackMutation.mutate({
      sentiment,
      message: message.trim(),
      page: pathname,
    });
  }, [sentiment, message, pathname, feedbackMutation]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        {submitted ? (
          <div className="flex flex-col items-center gap-2 py-6">
            <span className="text-2xl">&#10024;</span>
            <p className="text-foreground text-sm font-medium">Thanks for your feedback!</p>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>How&apos;s it going?</DialogTitle>
              <DialogDescription>Your feedback helps us improve Chamfer.</DialogDescription>
            </DialogHeader>

            {/* Sentiment row */}
            <div className="flex justify-center gap-2 py-1">
              {sentiments.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => {
                    setSentiment(s.key);
                  }}
                  title={s.label}
                  className={cn(
                    "flex size-12 items-center justify-center rounded-xl text-xl transition-all",
                    sentiment === s.key
                      ? "bg-primary/10 ring-primary/30 scale-110 ring-1"
                      : "hover:bg-muted",
                  )}
                >
                  {s.emoji}
                </button>
              ))}
            </div>

            {/* Message */}
            <textarea
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
              }}
              placeholder="Tell us more (optional)..."
              rows={2}
              className="border-input placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 w-full resize-none rounded-lg border bg-transparent px-2.5 py-2 text-sm transition-colors outline-none focus-visible:ring-3"
            />

            <DialogFooter>
              <Button
                onClick={handleSubmit}
                disabled={!sentiment || feedbackMutation.isPending}
                size="sm"
              >
                {feedbackMutation.isPending ? "Sending..." : "Send feedback"}
                <Send data-icon="inline-end" className="size-3.5" />
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function FeedbackWidget() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setOpen(true);
        }}
        className="fixed right-6 bottom-6 z-40 hidden size-10 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-lg transition-all hover:shadow-xl active:scale-95 lg:flex"
        aria-label="Send feedback"
      >
        <MessageSquarePlus className="size-4" />
      </button>
      <FeedbackDialog open={open} onOpenChange={setOpen} />
    </>
  );
}
